import { useState } from 'react';
import { useFiltrosUrl } from '@/hooks/useFiltrosUrl';
import { ESQUEMA_TRACKING } from '@/lib/filtros-esquemas';
import { useQuery } from '@tanstack/react-query';
import { MapPinned, RefreshCw, CalendarClock, Pencil } from 'lucide-react';
import * as trackingApi from '@/api/tracking.api';
import * as jobsApi from '@/api/jobs.api';
import { EditarJobModal } from '@/components/jobs/EditarJobModal';
import * as installationApi from '@/api/installation.api';
import type { TrackingTechnician, TrackingDayJob, TrackingCenter } from '@/api/tracking.api';
import { Select } from '@/components/ui/Select';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Avatar } from '@/components/ui/Avatar';
import { EmptyState } from '@/components/ui/EmptyState';
import { Button } from '@/components/ui/Button';
import { PageHeader } from '@/components/common/PageHeader';
import { MapaTracking, type MapPinData } from '@/components/tracking/MapaTracking';
import { JobTimeline } from '@/components/tracking/JobTimeline';
import { JOB_DAY_STATUS_META, scheduledHourLabel } from '@/lib/job-day-status';
import { jobStatusBadge } from '@/lib/badges';
import { relativeTime } from '@/lib/format';
import { formatDistanceM } from '@conversia/shared-types';
import { isLocationLive } from '@/lib/live-location';
import { rutaDelDia, tecnicoDayTimeline } from '@/lib/tecnico-dia';
import { cn } from '@/lib/cn';
import { FiltrosGuardados } from '@/components/filtros/FiltrosGuardados';
import { Cargando } from '@/components/common/Cargando';

/** Etiqueta + tono de la zona del trabajo actual del técnico. */
function zoneBadge(enZona: boolean | null): { label: string; variant: 'success' | 'danger' | 'default' } {
  if (enZona === true) return { label: 'Dentro de zona', variant: 'success' };
  if (enZona === false) return { label: 'Fuera de zona', variant: 'danger' };
  return { label: 'Sin objetivo', variant: 'default' };
}

/**
 * Pines del mapa: los TRABAJOS DE HOY (color por estado: en camino / en sitio /
 * vencido / completado) + la última ubicación de cada técnico. Trabajos sin
 * coordenadas quedan solo en la lista (degradación, nunca rompe).
 */
function buildPins(
  techs: TrackingTechnician[],
  jobs: TrackingDayJob[],
  centers: TrackingCenter[],
): MapPinData[] {
  const pins: MapPinData[] = [];
  // Centros de operación (bases): referencia fija en violeta, con el círculo
  // del radio. Van primero para que queden debajo de trabajos/técnicos.
  for (const c of centers) {
    pins.push({
      id: `center:${c.nombre}`,
      kind: 'center',
      label: c.nombre,
      sublabel: `Base · radio ${c.radioKm} km`,
      lat: c.lat,
      lng: c.lng,
      tone: 'brand',
      radiusM: c.radioKm * 1000,
    });
  }
  for (const j of jobs) {
    const meta = JOB_DAY_STATUS_META[j.status];
    const hora = scheduledHourLabel(j.scheduledAt);
    // Registro del técnico al cerrar (o motivo de no completado) visible en el popup.
    const detail = j.registroArticulo
      ? `Registro: ${j.registroArticulo}`
      : j.motivo
        ? `Motivo: ${j.motivo}`
        : undefined;
    pins.push({
      id: `job:${j.id}`,
      kind: 'job',
      label: `${j.cliente ?? j.descripcion}${hora ? ` · ${hora}` : ''}`,
      sublabel: `${meta.label}${j.tecnico ? ` · ${j.tecnico}` : ' · sin técnico'}`,
      detail,
      lat: j.lat,
      lng: j.lng,
      tone: meta.tone,
    });
  }
  for (const t of techs) {
    // Ubicación RECIENTE → pin azul que palpita (vivo); vieja → tono por zona.
    const vivo = isLocationLive(t.lastLocationAt);
    pins.push({
      id: t.id,
      kind: 'technician',
      label: t.nombre,
      sublabel: t.currentJob ? `En: ${t.currentJob.descripcion}` : 'Sin trabajo activo',
      lat: t.lastLat,
      lng: t.lastLng,
      tone: vivo
        ? 'info'
        : t.currentJob?.enZona === false
          ? 'warn'
          : t.currentJob?.enZona === true
            ? 'ok'
            : 'muted',
      pulse: vivo,
    });
  }
  return pins;
}

export default function Tracking() {
  const [focusedId, setFocusedId] = useState<string | null>(null);
  // A1: la zona vive en la URL.
  const { filtros, setFiltros } = useFiltrosUrl<{ zona?: string }>(ESQUEMA_TRACKING);
  const zona = filtros.zona ?? '';
  const setZona = (v: string) => setFiltros({ zona: v || undefined });
  // Edición desde la tarjeta del trabajo enfocado (el popup de Leaflet es HTML
  // plano: un formulario ahí no es limpio — la tarjeta es el lugar).
  const [editId, setEditId] = useState<string | null>(null);
  const { data: editJob } = useQuery({
    queryKey: ['jobs', 'edit', editId],
    queryFn: () => jobsApi.getJob(editId!),
    enabled: Boolean(editId),
  });

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['tracking'],
    queryFn: trackingApi.getTracking,
    refetchInterval: 30_000, // refresco periódico del panel
  });
  // Zonas CONFIGURABLES de la instalación (editable en /installation) — sin hardcode.
  const { data: crmView } = useQuery({
    queryKey: ['installation', 'crm-view'],
    queryFn: installationApi.getCrmView,
  });
  const zonaOptions = [
    { value: '', label: 'Zona: todas' },
    ...(crmView?.crm.zonas ?? []).map((z) => ({ value: z, label: z })),
  ];

  const allTechs = data?.technicians ?? [];
  const allJobs = data?.jobsToday ?? [];
  const centers = data?.centers ?? [];
  // Filtro por zona: trabajos por su zona; técnicos por tener trabajo del día
  // (o el actual) en esa zona. Al cambiar el set de pines, el mapa re-encuadra.
  const jobsToday = zona ? allJobs.filter((j) => j.zona === zona) : allJobs;
  const techs = zona
    ? allTechs.filter(
        (t) => t.currentJob?.zona === zona || jobsToday.some((j) => j.employeeId === t.id),
      )
    : allTechs;
  const pins = buildPins(techs, jobsToday, centers);
  const focused = techs.find((t) => t.id === focusedId) ?? null;
  // Trabajos del día del técnico enfocado (la unión va por employeeId, dato duro).
  const jobsDelTecnico = focused ? jobsToday.filter((j) => j.employeeId === focused.id) : [];
  // Trabajo del día enfocado (pin `job:<id>`): su tarjeta con la línea de tiempo.
  const focusedJob = focusedId?.startsWith('job:')
    ? jobsToday.find((j) => `job:${j.id}` === focusedId) ?? null
    : null;
  // Ruta del día del técnico enfocado: ponches en orden + su posición actual.
  const rutaTecnico = focused
    ? rutaDelDia(
        jobsDelTecnico,
        focused.lastLat != null && focused.lastLng != null
          ? { lat: focused.lastLat, lng: focused.lastLng }
          : null,
      )
    : undefined;

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Tracking de campo"
        helpKey="tracking"
        description="Los trabajos de hoy en el mapa, la ubicación de los técnicos y el chequeo de zona."
        action={
          <div className="flex items-center gap-2">
            <div className="w-44">
              <Select options={zonaOptions} value={zona} onChange={(e) => setZona(e.target.value)} />
            </div>
            <Button variant="outline" onClick={() => void refetch()} loading={isFetching}>
              <RefreshCw className="h-4 w-4" /> Actualizar
            </Button>
          </div>
        }
      />
      {/* A2 · Filtros guardados. Lee la ruta y el ?query de la URL por su cuenta. */}
      <FiltrosGuardados />

      {isLoading ? (
        <Cargando />
      ) : techs.length === 0 && jobsToday.length === 0 ? (
        <EmptyState icon={MapPinned} title="Sin técnicos ni trabajos hoy" />
      ) : (
        <div className="grid gap-6 lg:grid-cols-[1fr_1.2fr]">
          <div className="space-y-4">
            {/* Trabajos de HOY */}
            <Card>
              <CardBody className="p-0">
                <p className="flex items-center gap-1.5 px-5 pt-4 text-xs font-medium uppercase tracking-wide text-slate-400">
                  <CalendarClock className="h-3.5 w-3.5" /> Trabajos de hoy ({jobsToday.length})
                </p>
                {jobsToday.length === 0 ? (
                  <p className="px-5 py-4 text-sm text-slate-400">Sin trabajos agendados para hoy.</p>
                ) : (
                  <ul className="mt-2 divide-y divide-surface-border dark:divide-slate-800">
                    {jobsToday.map((j) => {
                      const meta = JOB_DAY_STATUS_META[j.status];
                      const hora = scheduledHourLabel(j.scheduledAt);
                      return (
                        <li
                          key={j.id}
                          onClick={() => setFocusedId(`job:${j.id}`)}
                          className={`flex cursor-pointer flex-wrap items-center gap-3 px-5 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 ${
                            focusedId === `job:${j.id}` ? 'bg-brand-50 dark:bg-brand-900/20' : ''
                          }`}
                        >
                          <div className="w-14 shrink-0 text-sm font-medium text-slate-700 dark:text-slate-300">
                            {hora ?? '—'}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                              {j.cliente ?? j.descripcion}
                            </p>
                            <p className="truncate text-xs text-slate-400">
                              {j.tecnico ?? 'Sin técnico'} · {j.zona ?? j.direccion ?? 'sin dirección'}
                              {j.lat == null && ' · sin ubicación'}
                            </p>
                          </div>
                          <Badge variant={meta.badge}>{meta.label}</Badge>
                          {/* Timeline al seleccionar (tanda ajustes-finales):
                              mismo componente del popup del mapa. */}
                          {focusedId === `job:${j.id}` && (
                            <JobTimeline estado={j.estado} className="w-full pt-1" />
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CardBody>
            </Card>

            {/* Técnicos */}
            <Card>
              <CardBody className="p-0">
                <p className="px-5 pt-4 text-xs font-medium uppercase tracking-wide text-slate-400">
                  Técnicos ({techs.length})
                </p>
                <ul className="mt-2 divide-y divide-surface-border dark:divide-slate-800">
                  {techs.map((t) => {
                    const job = t.currentJob;
                    const z = zoneBadge(job?.enZona ?? null);
                    const estado = job ? jobStatusBadge(job.estado) : null;
                    return (
                      <li
                        key={t.id}
                        onClick={() => setFocusedId(t.id)}
                        className={`flex cursor-pointer flex-wrap items-center gap-3 px-5 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 ${
                          focusedId === t.id ? 'bg-brand-50 dark:bg-brand-900/20' : ''
                        }`}
                      >
                        <Avatar name={t.nombre} src={t.fotoUrl} size="md" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                            {t.nombre} {t.nivel && <span className="text-xs text-slate-400">· Nivel {t.nivel}</span>}
                          </p>
                          <p className="truncate text-xs text-slate-400">
                            {t.lastLat != null
                              ? `Últ. ubicación ${relativeTime(t.lastLocationAt)}`
                              : 'Sin ubicación reportada'}
                          </p>
                          {t.nearestCenter && (
                            <p
                              className={cn(
                                'truncate text-xs',
                                t.nearestCenter.dentro
                                  ? 'text-emerald-600 dark:text-emerald-400'
                                  : 'text-slate-500 dark:text-slate-400',
                              )}
                            >
                              {t.nearestCenter.dentro
                                ? `En base ${t.nearestCenter.nombre}`
                                : `A ${t.nearestCenter.distanciaKm} km de ${t.nearestCenter.nombre}`}
                            </p>
                          )}
                          <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                            {job ? job.descripcion : 'Sin trabajo activo'}
                          </p>
                        </div>
                        {estado && <Badge variant={estado.variant}>{estado.label}</Badge>}
                        {job && <Badge variant={z.variant}>{z.label}</Badge>}
                      </li>
                    );
                  })}
                </ul>
              </CardBody>
            </Card>
          </div>

          {/* Mapa + detalle del pin enfocado (técnico o trabajo) */}
          <div className="space-y-4">
            <MapaTracking
              pins={pins}
              focusedId={focusedId}
              onSelect={(id) => setFocusedId(id)}
              route={rutaTecnico}
            />
            {focusedJob && (
              <Card>
                <CardBody className="space-y-3 text-sm">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-slate-900 dark:text-slate-100">
                        {focusedJob.cliente ?? focusedJob.descripcion}
                      </p>
                      <p className="truncate text-xs text-slate-400">
                        {[
                          scheduledHourLabel(focusedJob.scheduledAt),
                          focusedJob.tecnico ?? 'Sin técnico',
                          focusedJob.zona ?? focusedJob.direccion,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <Badge variant={JOB_DAY_STATUS_META[focusedJob.status].badge}>
                        {JOB_DAY_STATUS_META[focusedJob.status].label}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Editar trabajo"
                        title="Editar trabajo"
                        className="text-slate-400 hover:text-brand-600"
                        onClick={() => setEditId(focusedJob.id)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <JobTimeline estado={focusedJob.estado} />
                  {(focusedJob.registroArticulo || focusedJob.motivo) && (
                    <p className="text-xs text-slate-600 dark:text-slate-300">
                      {[
                        focusedJob.registroArticulo
                          ? `Registro del técnico: “${focusedJob.registroArticulo}”`
                          : null,
                        focusedJob.motivo ? `Motivo: ${focusedJob.motivo}` : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  )}
                </CardBody>
              </Card>
            )}
            {focused && (
              <Card>
                <CardBody className="space-y-3 text-sm">
                  {/* Tarjeta del técnico (tipo Uber): foto, nombre y sus trabajos del día. */}
                  <div className="flex items-center gap-3">
                    <Avatar name={focused.nombre} src={focused.fotoUrl} size="lg" />
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 dark:text-slate-100">
                        {focused.nombre}
                        {focused.nivel && (
                          <span className="ml-1 text-xs font-normal text-slate-400">Nivel {focused.nivel}</span>
                        )}
                      </p>
                      <p className="text-xs text-slate-400">
                        {focused.lastLat != null
                          ? `Última ubicación ${relativeTime(focused.lastLocationAt)}`
                          : 'Sin ubicación reportada'}
                        {isLocationLive(focused.lastLocationAt) && (
                          <span className="ml-1 text-blue-500">· en vivo</span>
                        )}
                      </p>
                    </div>
                  </div>
                  {focused.nearestCenter && (
                    <p className="text-xs">
                      {focused.nearestCenter.dentro ? (
                        <span className="text-emerald-600 dark:text-emerald-400">
                          En base {focused.nearestCenter.nombre}
                        </span>
                      ) : (
                        <span className="text-slate-500 dark:text-slate-400">
                          A {focused.nearestCenter.distanciaKm} km de {focused.nearestCenter.nombre}{' '}
                          (base más cercana)
                        </span>
                      )}
                    </p>
                  )}
                  {focused.currentJob && (
                    <p className="text-xs">
                      {focused.currentJob.enZona === true && <span className="text-emerald-600 dark:text-emerald-400">Dentro de zona</span>}
                      {focused.currentJob.enZona === false && <span className="text-danger">Fuera de zona</span>}
                      {focused.currentJob.enZona == null && <span className="text-slate-400">Sin objetivo de zona</span>}
                      {focused.currentJob.distanceM != null && ` · ${formatDistanceM(focused.currentJob.distanceM)} del objetivo`}
                    </p>
                  )}
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                      Su día ({jobsDelTecnico.length} trabajo{jobsDelTecnico.length === 1 ? '' : 's'})
                    </p>
                    {jobsDelTecnico.length === 0 ? (
                      <p className="mt-1 text-xs text-slate-400">Sin trabajos asignados hoy.</p>
                    ) : (
                      // Línea de tiempo del DÍA (tipo Uber): secuencia con hora y estado.
                      <ol className="mt-2">
                        {tecnicoDayTimeline(jobsDelTecnico).map((paso, i, arr) => (
                          <li key={paso.job.id} className="flex gap-2">
                            <div className="flex w-4 flex-col items-center">
                              <span
                                className={cn(
                                  'mt-1 h-3 w-3 shrink-0 rounded-full border-2 border-white dark:border-slate-900',
                                  paso.estado === 'completado' && 'bg-emerald-500',
                                  paso.estado === 'en_curso' && 'animate-pulse bg-blue-500',
                                  paso.estado === 'pendiente' && 'bg-slate-300 dark:bg-slate-600',
                                  paso.estado === 'no_completado' && 'bg-red-500',
                                  paso.estado === 'cancelado' && 'bg-slate-400',
                                )}
                              />
                              {i < arr.length - 1 && (
                                <span className="w-0.5 flex-1 bg-slate-200 dark:bg-slate-700" />
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => setFocusedId(`job:${paso.job.id}`)}
                              className="mb-2 min-w-0 flex-1 rounded px-1 pb-1 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50"
                            >
                              <span className="flex items-center gap-2">
                                <span className="text-xs font-medium text-slate-500">
                                  {scheduledHourLabel(paso.horaIso) ?? '—'}
                                  {paso.horaReal ? '' : ' (cita)'}
                                </span>
                                <Badge variant={JOB_DAY_STATUS_META[paso.job.status].badge}>
                                  {JOB_DAY_STATUS_META[paso.job.status].label}
                                </Badge>
                              </span>
                              <span className="block truncate text-xs text-slate-700 dark:text-slate-200">
                                {paso.job.cliente ?? paso.job.descripcion}
                              </span>
                            </button>
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>
                </CardBody>
              </Card>
            )}
          </div>
        </div>
      )}

      <EditarJobModal job={editId ? editJob ?? null : null} onClose={() => setEditId(null)} />
    </div>
  );
}
